/**
 * Cache utility functions for localStorage-based caching
 * Supports multiple caching strategies: permanent, daily, and period-based
 */

/**
 * Get current 30-minute period key (rounds to nearest hour or half-hour)
 * Returns format: YYYY-MM-DDTHH:00 or YYYY-MM-DDTHH:30
 */
export const getCurrentPeriodKey = (): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = minutes < 30 ? 0 : 30;
  const dateStr = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
  const periodStr = dateStr.slice(0, 14) + (roundedMinutes === 0 ? '00' : '30'); // YYYY-MM-DDTHH:00 or YYYY-MM-DDTHH:30
  return periodStr;
};

/**
 * Get cache date key for daily caching (refreshes daily after specified hour)
 * @param prefix - Cache key prefix
 * @param refreshHour - Hour of day when cache should refresh (default: 6)
 * @returns Cache key with date suffix
 */
export const getDailyCacheKey = (prefix: string, refreshHour: number = 6): string => {
  const now = new Date();
  const hour = now.getHours();
  // If before refresh hour, use yesterday's date, otherwise use today's date
  const date = hour < refreshHour 
    ? new Date(now.getTime() - 24 * 60 * 60 * 1000)
    : now;
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  return `${prefix}-${dateStr}`;
};

/**
 * Create a cache key from URLSearchParams
 * Returns both the full cache key base and the prefix for cleanup
 * @param prefix - Cache key prefix
 * @param params - URLSearchParams object containing the query parameters
 * @returns Object with keyBase (for use with getDailyCacheKey) and prefix (for cleanup)
 */
export const createCacheKeyFromParams = (prefix: string, params: URLSearchParams): { keyBase: string; prefix: string } => {
  // Sort params to ensure consistent keys regardless of param order
  const sortedParams = Array.from(params.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  
  // Create a simple hash from the params string
  let hash = 0;
  for (let i = 0; i < sortedParams.length; i++) {
    const char = sortedParams.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use absolute value and convert to hex for a cleaner key
  const hashStr = Math.abs(hash).toString(16);
  return {
    keyBase: `${prefix}-${hashStr}`,
    prefix: prefix
  };
};

/**
 * Get cached data from localStorage
 * @param cacheKey - The cache key to retrieve
 * @returns Cached data or null if not found/invalid
 */
export const getCachedData = <T,>(cacheKey: string): T | null => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached) as T;
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

/**
 * Store data in cache (permanent - no expiration)
 * @param cacheKey - The cache key to store
 * @param data - The data to cache
 */
export const setCachedData = <T,>(cacheKey: string, data: T): void => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (e) {
    // Ignore cache errors (e.g., quota exceeded)
  }
};

/**
 * Get cached data with period validation (for 30-minute period caching)
 * @param cacheKey - The cache key to retrieve
 * @returns Cached data or null if not found/invalid/expired
 */
export const getPeriodCachedData = <T,>(cacheKey: string): T | null => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is from current period
      const cachePeriod = parsed.period;
      const currentPeriod = getCurrentPeriodKey();
      if (cachePeriod === currentPeriod) {
        return parsed.data as T;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

/**
 * Store data in cache with period information (for 30-minute period caching)
 * @param cacheKey - The cache key to store
 * @param data - The data to cache
 */
export const setPeriodCachedData = <T,>(cacheKey: string, data: T): void => {
  try {
    const currentPeriod = getCurrentPeriodKey();
    const cacheValue = {
      period: currentPeriod,
      data: data
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheValue));
  } catch (e) {
    // Ignore cache errors (e.g., quota exceeded)
  }
};

/**
 * Clean up old period-based cache entries
 * @param keyPrefixes - Array of key prefixes to clean up (e.g., ['electricity-rates-', 'carbon-intensity-'])
 */
export const cleanupPeriodCache = (keyPrefixes: string[]): void => {
  try {
    const currentPeriod = getCurrentPeriodKey();
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && keyPrefixes.some(prefix => key.startsWith(prefix))) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const parsed = JSON.parse(cached);
            // If the cached period is not the current period, mark for removal
            if (parsed.period && parsed.period !== currentPeriod) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // If we can't parse it, it's corrupted - remove it
          keysToRemove.push(key);
        }
      }
    }
    removeItems(keysToRemove);
  } catch (e) {
    // Ignore cleanup errors
  }
};

/**
 * Clean up old daily cache entries (keeps only current and previous day)
 * @param keyPrefix - The key prefix to clean up (e.g., 'most-recent-complete-day-type-1')
 * @param refreshHour - Hour of day when cache refreshes (default: 6)
 */
export const cleanupDailyCache = (keyPrefix: string, refreshHour: number = 6): void => {
  try {
    const now = new Date();
    const hour = now.getHours();
    const currentDate = hour < refreshHour 
      ? new Date(now.getTime() - 24 * 60 * 60 * 1000) // Yesterday if before refresh hour
      : now; // Today if after refresh hour
    const currentDateStr = currentDate.toISOString().split('T')[0];
    const previousDate = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
    const previousDateStr = previousDate.toISOString().split('T')[0];
    
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(keyPrefix)) {
        // Extract date from key (format: prefix-YYYY-MM-DD)
        const keyDateStr = key.split('-').slice(-3).join('-'); // Get last 3 parts (YYYY-MM-DD)
        
        // If the date is not current or previous, mark for removal
        if (keyDateStr !== currentDateStr && keyDateStr !== previousDateStr) {
          keysToRemove.push(key);
        }
      }
    }
    removeItems(keysToRemove);
  } catch (e) {
    // Ignore cleanup errors
  }
};

/**
 * Store data in daily cache with automatic cleanup
 * @param cacheKey - The cache key to store
 * @param data - The data to cache
 * @param keyPrefix - The key prefix for cleanup (e.g., 'most-recent-complete-day-type-1')
 * @param refreshHour - Hour of day when cache refreshes (default: 6)
 */
export const setDailyCachedData = <T,>(
  cacheKey: string, 
  data: T, 
  keyPrefix: string, 
  refreshHour: number = 6
): void => {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    
    // Clean up old entries
    cleanupDailyCache(keyPrefix, refreshHour);
  } catch (e) {
    // Ignore cache errors (e.g., quota exceeded)
  }
};

/**
 * Clear cached values for earliestReading and mostRecentCompleteDay
 * This is useful when user upgrades to paid subscription and data access changes
 */
export const clearDateRangeCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Remove earliest-reading cache (permanent cache, no date suffix)
        if (key.startsWith('earliest-reading-')) {
          keysToRemove.push(key);
        }
        // Remove most-recent-complete-day cache (daily cache with date suffix)
        if (key.startsWith('most-recent-complete-day-')) {
          keysToRemove.push(key);
        }
      }
    }
    removeItems(keysToRemove);
  } catch (e) {
    // Ignore cleanup errors
  }
};

/**
 * Clear all consumption-related cache entries
 * This should be called when Octopus connection is deleted or changed
 */
export const clearConsumptionCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    // Consumption-related cache prefixes
    const consumptionPrefixes = [
      'userconsumption-get',
      'userconsumption-peak-hours-percentage',
      'userconsumption-standing-charges',
      'userconsumption-histogram-percentiles',
      'tariff-data'
    ];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if key starts with any consumption prefix
        if (consumptionPrefixes.some(prefix => key.startsWith(prefix))) {
          keysToRemove.push(key);
        }
      }
    }
    removeItems(keysToRemove);
  } catch (e) {
    // Ignore cleanup errors
  }
};

/**
 * Clear cached user data (api/users/current and api/users/account)
 * This should be called on logout, login, and when user details are updated
 */
export const clearUserCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Remove user cache entries
        if (key === 'user-current' || key === 'user-account' || key === 'energy-insights-prefs') {
          keysToRemove.push(key);
        }
        // Remove dashboard preferences
        if (key.startsWith('dashboard-prefs-')) {
          keysToRemove.push(key);
        }

        if (key.startsWith('most-recent-complete-day')) {
          keysToRemove.push(key);
        }

        if (key.startsWith('earliest-reading')) {
          keysToRemove.push(key);
        }
      }
    }
    removeItems(keysToRemove);
  } catch (e) {
    // Ignore cleanup errors
  }
};

export const removeItems = (keysToRemove: string[]): void => {
    // Remove all matching entries
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Could not remove ${key} from local storage.`)
      }
    });
};

/**
 * Dashboard preferences interface
 */
export interface DashboardPreferences {
  dateFrom: string;
  dateTo: string;
  aggregation: string;
}

/**
 * Energy Insights preferences interface
 */
export interface EnergyInsightsPreferences {
  viewMode: 'simplified' | 'detailed';
}

/**
 * Get dashboard preferences from cache
 * @param dashboardName - Name of the dashboard ('electricity', 'gas', or 'emissions')
 * @returns Cached preferences or null if not found
 */
export const getDashboardPreferences = (dashboardName: string): DashboardPreferences | null => {
  try {
    const cacheKey = `dashboard-prefs-${dashboardName}`;
    return getCachedData<DashboardPreferences>(cacheKey);
  } catch (e) {
    return null;
  }
};

/**
 * Save dashboard preferences to cache
 * @param dashboardName - Name of the dashboard ('electricity', 'gas', or 'emissions')
 * @param preferences - The preferences to cache
 */
export const setDashboardPreferences = (dashboardName: string, preferences: DashboardPreferences): void => {
  try {
    const cacheKey = `dashboard-prefs-${dashboardName}`;
    setCachedData(cacheKey, preferences);
  } catch (e) {
    // Ignore cache errors
  }
};

/**
 * Get Energy Insights preferences from cache
 * @returns Cached preferences or null if not found
 */
export const getEnergyInsightsPreferences = (): EnergyInsightsPreferences | null => {
  try {
    const cacheKey = 'energy-insights-prefs';
    return getCachedData<EnergyInsightsPreferences>(cacheKey);
  } catch (e) {
    return null;
  }
};

/**
 * Save Energy Insights preferences to cache
 * @param preferences - The preferences to cache
 */
export const setEnergyInsightsPreferences = (preferences: EnergyInsightsPreferences): void => {
  try {
    const cacheKey = 'energy-insights-prefs';
    setCachedData(cacheKey, preferences);
  } catch (e) {
    // Ignore cache errors
  }
};
